// Rensselaer Polytechnic Institute: private college page (#2839), official
// facts only. The slug keeps the KB id (rensselaer_polytechnic_institute_profile).
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - RPI's Common Data Set 2025-2026 (Office of the Provost, Institutional
//    Research and Assessment; the PDF sits on rpi.box.com and the source link
//    is the provost.rpi.edu page that lists every edition, 2025-2026 the
//    newest): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table (Unknown is 0 and left out), C8 test policy for Fall
//    2027, C21/C22 plans, G1/G5 2026-2027 costs. Departure: the C21 early
//    decision counts (317 applied, 219 admitted) sit under a line that still
//    reads "For the Fall 2024 entering class"; the 2024-2025 edition gives
//    different counts (240 and 139) under the same line, so these are read
//    as the Fall 2025 class. The C1 check-box lines for full-time and
//    part-time enrollees are misaligned in the PDF and are not used.
//  - RPI Undergraduate Admissions: Apply (the "Application Options - Fall
//    2027" table, the B.S./M.D. and Architecture requirements, the fee),
//    Applying Early (the binding agreement), Prospective Students (test
//    policy, test-optional through the Fall 2030 application cycle). The
//    Applying Early page gives "Mid-December" and "Late January" for the two
//    early decision rounds; the Apply page's dates (December 12, January 16)
//    are used.
//  - RPI Office of Financial Aid, Cost of Attendance 2026-2027: the same
//    $90,076 total the CDS gives; the aid office's items are used (it lists
//    food and housing as one line, $18,870, the CDS's $9,850 housing plus
//    $9,020 food).
//  - College Scorecard API, school id 194824 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'RPI Common Data Set 2025-2026',
        url: 'https://provost.rpi.edu/institutional-research-and-assessment',
        read: '2026-10-01',
    },
    apply: {
        label: 'RPI Undergraduate Admissions, Apply',
        url: 'https://undergrad.admissions.rpi.edu/apply',
        read: '2026-10-01',
    },
    early: {
        label: 'RPI Undergraduate Admissions, Applying Early',
        url: 'https://undergrad.admissions.rpi.edu/apply/applying-early',
        read: '2026-10-01',
    },
    testing: {
        label: 'RPI Undergraduate Admissions, Prospective Students: Standardized Test Requirements',
        url: 'https://undergrad.admissions.rpi.edu/prospective-students',
        read: '2026-10-01',
    },
    coa: {
        label: 'RPI Office of Financial Aid, Cost of Attendance',
        url: 'https://financialaid.rpi.edu/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?194824-Rensselaer-Polytechnic-Institute',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'rensselaer-polytechnic-institute-profile',
    kbId: 'rensselaer_polytechnic_institute_profile',
    name: 'Rensselaer Polytechnic Institute',
    shortName: 'RPI',
    aliases: ['RPI', 'Rensselaer'],
    location: 'Troy, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (RPI brand colors); decoration only.
    branding: {
        primary: '#D6001C',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rensselaer_polytechnic_institute_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 14356,
        admitted: 9655,
        enrolled: 1394,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4319, admitted: 2917 },
            { group: 'Out-of-state', applied: 8947, admitted: 6125 },
            { group: 'International', applied: 1090, admitted: 613 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'RPI\'s Common Data Set reports one first-year acceptance rate for the institute, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'RPI has two binding early decision rounds and a non-binding Early Action plan that does not limit where else you apply early. The Common Data Set gives one early decision count for both rounds together and no early action counts. Its line still says "Fall 2024 entering class", but the counts differ from the prior edition\'s, so they are shown as Fall 2025.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision (I and II)', applied: 317, admitted: 219 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to RPI with the Common App or the RPI Application. Two programs ask for more:',
        rows: [
            { school: 'Physician-Scientist (B.S./M.D.) Program', requirement: 'Apply by November 1; decisions come in early April. Official SAT or ACT scores are required, and a writing supplement asks why you want a career in medicine.', source: 'apply' },
            { school: 'Architecture (B.Arch.)', requirement: 'A creative portfolio, sent within 10 days of the deadline for your application plan, and a writing supplement on why you chose architecture. No other major takes a portfolio.', source: 'apply' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 12', binding: true },
            { plan: 'Early Action', deadline: 'December 1', decision: 'January 30', binding: false },
            { plan: 'Early Decision II', deadline: 'January 6', decision: 'January 16', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 6', binding: false },
            { plan: 'Physician-Scientist (B.S./M.D.)', deadline: 'November 1', decision: 'Early April', binding: false },
        ],
        note: 'The application fee is $70, with no fee for applications sent by December 1. Early Decision applicants send first-quarter grades as soon as they have them. If you want need-based aid, the FAFSA and CSS Profile priority dates are November 15 (Early Decision I and B.S./M.D.), December 15 (Early Action), January 8 (Early Decision II) and January 31 (Regular Decision).',
        source: 'apply',
        binding: 'Early Decision is binding: if admitted, you commit to attending RPI and withdraw your other applications, provided the aid offered meets your needs.',
        bindingSource: 'early',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'RPI lets fall 2027 applicants choose whether to send SAT or ACT scores, and says it will remain test-optional through the Fall 2030 application cycle. Physician-Scientist (B.S./M.D.) applicants must send official scores. RPI superscores the SAT and uses the highest ACT composite; you can self-report scores and send official ones if you enroll.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required for some applicants and otherwise not required but considered if submitted, for fall 2027 applicants, with scores due by December 31. It names the Accelerated Physician-Scientist Program as the one that requires them.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'No change: RPI says it will remain test-optional through the Fall 2030 application cycle, which covers fall 2028 applicants.', source: 'testing' },
        ],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 66300 },
            { item: 'Fees', amount: 1676 },
            { item: 'Food and housing', amount: 18870 },
            { item: 'Books and supplies', amount: 1410 },
            { item: 'Personal expenses', amount: 1280 },
            { item: 'Transportation', amount: 540 },
        ],
        total: 90076,
        note: 'For a full-time undergraduate, fall and spring; the Common Data Set gives the same total. RPI uses the same budget on and off campus unless you live with family. Not in the total: required health insurance ($2,160 for 2026-27, waived if you have family coverage) and a required laptop (about $2,000 if bought from RPI).',
        source: 'coa',
        netPrice: {
            average: 32758,
            byIncome: [
                { income: '$0 to $30,000', amount: 25140 },
                { income: '$30,001 to $48,000', amount: 25313 },
                { income: '$48,001 to $75,000', amount: 31222 },
                { income: '$75,001 to $110,000', amount: 33659 },
                { income: 'Over $110,000', amount: 35346 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from RPI\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
